import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Star } from "lucide-react";
import api, { formatApiError, assetUrl } from "../../lib/api";
import SEO from "../../components/site/SEO";
import { inputCls, labelCls, btnPrimaire, confirmerSuppression } from "./ui";

export default function AdminPhotos() {
  const [photos, setPhotos] = useState([]);
  const [nom, setNom] = useState("");
  const [fichier, setFichier] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const inputFichier = useRef(null);

  const charger = () => api.get("/admin/photos").then((r) => setPhotos(r.data)).catch(() => {});
  useEffect(() => { charger(); }, []);

  const envoyer = async (e) => {
    e.preventDefault();
    if (!fichier) {
      toast.error("Choisissez d'abord une photo sur votre appareil.");
      return;
    }
    setEnvoi(true);
    try {
      const data = new FormData();
      data.append("nom", nom || fichier.name);
      data.append("fichier", fichier);
      await api.post("/admin/photos", data);
      toast.success("Votre photo a été ajoutée à la bibliothèque.");
      setNom("");
      setFichier(null);
      if (inputFichier.current) inputFichier.current.value = "";
      charger();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setEnvoi(false);
    }
  };

  const definirHero = async (p) => {
    await api.put(`/admin/photos/${p.id}/hero`);
    toast.success("Cette photo s'affiche maintenant en grand sur la page d'accueil.");
    charger();
  };

  const supprimer = async (p) => {
    if (!confirmerSuppression(p.nom)) return;
    await api.delete(`/admin/photos/${p.id}`);
    toast.success("Photo supprimée.");
    charger();
  };

  return (
    <div data-testid="admin-photos" className="max-w-4xl">
      <SEO title="Photos — Espace gérant | Promo Pneus" description="Gérer les photos du site." />
      <h1 className="font-heading font-bold uppercase text-4xl text-brand-dark tracking-tight mb-2">Photos</h1>
      <p className="text-brand-body text-lg mb-8">
        Vos photos sont automatiquement adaptées au site. Rien à régler de votre côté.
      </p>

      <form onSubmit={envoyer} className="bg-white p-6 shadow-sm mb-10 space-y-4" data-testid="photo-formulaire">
        <div>
          <label htmlFor="photo-fichier" className={labelCls}>Choisir une photo</label>
          <input id="photo-fichier" data-testid="photo-fichier" ref={inputFichier} type="file" accept="image/*"
            onChange={(e) => setFichier(e.target.files[0] || null)}
            className="w-full font-body text-brand-dark file:bg-brand-dark file:text-white file:font-heading file:uppercase file:px-5 file:py-3 file:border-0 file:mr-4 file:cursor-pointer" />
        </div>
        <div>
          <label htmlFor="photo-nom" className={labelCls}>Nom de la photo (pour vous repérer)</label>
          <input id="photo-nom" data-testid="photo-nom" className={inputCls} value={nom}
            onChange={(e) => setNom(e.target.value)} placeholder="Ex. : La devanture en été" />
        </div>
        <button type="submit" data-testid="photo-envoyer" disabled={envoi} className={`${btnPrimaire} inline-flex items-center gap-2`}>
          <Upload size={20} aria-hidden="true" />
          {envoi ? "Envoi en cours…" : "Ajouter la photo"}
        </button>
      </form>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {photos.map((p) => (
          <div key={p.id} data-testid={`photo-carte-${p.id}`} className="bg-white shadow-sm">
            <img src={assetUrl(p.url)} alt={p.nom} className="w-full h-48 object-cover" loading="lazy" />
            <div className="p-4">
              <p className="font-heading font-semibold uppercase text-lg text-brand-dark mb-3">
                {p.nom}
                {p.hero && (
                  <span className="ml-2 inline-flex items-center gap-1 text-brand-red text-sm">
                    <Star size={16} aria-hidden="true" /> Affichée sur l'accueil
                  </span>
                )}
              </p>
              <div className="flex gap-2">
                {!p.hero && (
                  <button data-testid={`photo-hero-${p.id}`} onClick={() => definirHero(p)}
                    className="flex-1 bg-brand-red hover:bg-brand-redhover text-white font-heading font-semibold uppercase tracking-wider py-3 text-base transition-colors">
                    Afficher sur l'accueil
                  </button>
                )}
                <button data-testid={`photo-supprimer-${p.id}`} onClick={() => supprimer(p)}
                  className="p-3 bg-red-50 hover:bg-red-100 text-brand-redhover transition-colors" aria-label={`Supprimer ${p.nom}`}>
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
